
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { globalStore } from "@/store/store";
import { CSSSelector, SelectorCSSName } from "@/types/selectors";
import SelectorsList from "@/components/Settings/Selectors/SelectorsList.vue";
import AddClass from "./AddClass.vue";
import {
  addSelectorToStoreAndSaveSelectorsToWordPressIfSelectorsPanelIsClosed,
  isValidClassName
} from "@/lib/selectors/selectors";
import defensiveCopy from "@/util/defensive-copy";
import NotificationLogger from "@/lib/notifications/notifications";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import FreeModeAlertForSelectors from "@/components/Subscription/FreeModeAlertForSelectors.vue";

@Component({
  components: {
    FreeModeAlertForSelectors,
    AlertBox,
    SelectorsList,
    AddClass
  }
})
export default class ClassInput extends Vue {
  @Prop()
  value: SelectorCSSName[] | undefined;

  get internalValue() {
    /** add the preceding dots back to the selector names */
    return (this.value ? this.value : []).map(
      classNameWithoutDot => "." + classNameWithoutDot
    );
  }

  get cssClasses() {
    return globalStore.cssSelectors;
  }

  get selectors() {
    /*
    we pass the selectors that have been added to the element to the ClassList component
    if a class is present on the element that has been deleted from the global store, it wont
    be returned by this function - so our class list will only show selectors that are actually
    present on the site.
    */
    return this.internalValue.reduce<CSSSelector[]>((acc, className) => {
      const foundClass = this.cssClasses.find(
        selector => selector.name === className
      );
      return foundClass ? [...acc, foundClass] : [...acc];
    }, []);
  }

  get missingSelectorsFriendly() {
    if (this.missingSelectors.length === 1) {
      return this.missingSelectors[0];
    }
    return `${this.missingSelectors
      .slice(0, -1)
      .join(", ")} and ${this.missingSelectors.slice(-1)}`;
  }

  get missingSelectors() {
    /* selectors present on the element but not present in the store */
    return this.internalValue.reduce<string[]>((acc, className) => {
      const foundClass = this.cssClasses.find(
        selector => selector.name === className
      );
      return !foundClass ? [...acc, className] : [...acc];
    }, []);
  }

  addClass(classNameMaybeWithoutDot: string) {
    let currentClassesOnElement = defensiveCopy(this.internalValue);

    const newClassName = classNameMaybeWithoutDot.startsWith(".")
      ? classNameMaybeWithoutDot
      : `.${classNameMaybeWithoutDot}`;

    if (!isValidClassName(newClassName)) {
      NotificationLogger.log.message(
        `"${newClassName}" is not a valid class name.`
      );
      return;
    }

    /*
    when adding a class, first we remove all the classes that are no longer
    present on the site. for example, someone could add .horse to an element, then
    delete .horse from the manage classes/selectors panel, but it would still be on
    the element
    */
    currentClassesOnElement = currentClassesOnElement.filter(name =>
      this.cssClasses.find(c => c.name === name)
    );

    if (currentClassesOnElement.find(name => name === newClassName)) {
      NotificationLogger.log.message(
        `${newClassName} is already present on the element.`
      );
      return;
    }

    /* if the class isn't already present in the global store, create it */
    if (!this.cssClasses.find(c => c.name === newClassName)) {
      addSelectorToStoreAndSaveSelectorsToWordPressIfSelectorsPanelIsClosed({
        name: newClassName,
        type: "class",
        properties: {}
      });
    }

    this.emit([...currentClassesOnElement, newClassName]);
  }

  updateClasses(newSelectors: CSSSelector[]) {
    const newClassNames = newSelectors.map(selector => selector.name);
    this.emit(newClassNames);
  }

  refreshClasses() {
    const newClassNames = this.selectors.map(selector => selector.name);
    this.emit(newClassNames);
  }

  emit(newClassNames: SelectorCSSName[]) {
    /* de-duplicate - set only allows unique values */
    /* remove the preceding dots from the class names */
    const deduplicatedClassNamesWithNoDots = Array.from(
      new Set(newClassNames)
    ).map(classNameWithDot => classNameWithDot.substr(1));

    this.$emit("input", deduplicatedClassNamesWithNoDots);
  }
}
